/**
 * Warp mesh — Tier 1 Group E Phase 1b.
 *
 * Free Transform's Warp sub-mode deforms a layer through a 3×3
 * grid of control points. The user drags any of the 9 points;
 * the underlying pixels are resampled so they follow the mesh
 * deformation smoothly.
 *
 * Architecture:
 *   - 9 control points laid out in a 3×3 grid. The default
 *     (un-warped) state places them at evenly-spaced fractions
 *     of the source rect: (0,0), (0.5,0), (1,0), (0,0.5), ...
 *   - User drags translate individual points. The interpolated
 *     surface between them defines a continuous warp.
 *   - We interpolate with **Catmull-Rom** in 2D (bicubic across
 *     the 3×3 grid). Catmull-Rom is C1-continuous, passes through
 *     all control points, and doesn't require derivative input.
 *     Photoshop uses bicubic-Bezier for Warp; visually equivalent
 *     for typical drag ranges.
 *   - Rasterization: tessellate the unit square into a finer
 *     SUBDIV×SUBDIV grid (default 32), evaluate the interpolated
 *     deformation at each sub-cell corner, and draw each sub-cell
 *     as two affine triangles via canvas2d setTransform — same
 *     pattern as projective-transform.ts.
 *
 * Public surface:
 *   - `defaultWarpGrid(width, height)` — identity-warp grid.
 *   - `interpolateWarp(grid, u, v)` — evaluate the warp at
 *     normalized coords (u, v) ∈ [0, 1].
 *   - `rasterizeWarp(source, grid, target, opts)` — render the
 *     warped source onto target.
 *   - `applyWarpPreset(grid, preset, intensity)` — seed grid with
 *     Arc / Bulge / Twist / Wave configurations.
 */

import { affineFromTriangle, type Point2 } from './projective-transform';

/** A 3×3 grid of destination control points (in target canvas
 *  pixel coords). Index `row * 3 + col`, top-left first. The
 *  source corresponds to the unit square (0,0)→(1,1); each grid
 *  cell maps to (col*0.5, row*0.5)→((col+1)*0.5, (row+1)*0.5)
 *  in normalized source coords. */
export type WarpGrid = readonly [
  Point2,
  Point2,
  Point2,
  Point2,
  Point2,
  Point2,
  Point2,
  Point2,
  Point2,
];

/** Build the identity-warp grid for a source rect of (width × height):
 *  9 points laid out at the corners + edge midpoints + center. */
export function defaultWarpGrid(width: number, height: number): WarpGrid {
  return [
    { x: 0, y: 0 },
    { x: width / 2, y: 0 },
    { x: width, y: 0 },
    { x: 0, y: height / 2 },
    { x: width / 2, y: height / 2 },
    { x: width, y: height / 2 },
    { x: 0, y: height },
    { x: width / 2, y: height },
    { x: width, y: height },
  ] as const;
}

/** Catmull-Rom 1D interpolation across 4 points. `t` ∈ [0, 1]
 *  parameterizes between p1 and p2; p0 and p3 set the local
 *  tangents. With tension 0.5 (default), this matches the
 *  classic uniform Catmull-Rom spline. */
function catmullRom1D(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const t2 = t * t;
  const t3 = t2 * t;
  return (
    0.5 *
    (2 * p1 +
      (-p0 + p2) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
      (-p0 + 3 * p1 - 3 * p2 + p3) * t3)
  );
}

/** Evaluate the warp at normalized source coords (u, v) where
 *  (0, 0) is the top-left corner and (1, 1) is the bottom-right.
 *  Returns the destination point in target pixel coords.
 *
 *  Implementation: bicubic Catmull-Rom across the 3×3 grid. With
 *  only 3 control points along each axis we need a virtual 4th
 *  for the cubic — we extrapolate it via 2*last - secondLast,
 *  which gives a natural linear extension at the boundaries
 *  (matches the visual expectation when control points lie at
 *  the edge). */
export function interpolateWarp(grid: WarpGrid, u: number, v: number): Point2 {
  // Sample 4×4 control net by extrapolating the missing
  // boundary row/col from the 3×3 grid. Pattern:
  //   c[i,j]  = grid[clamp(i,0,2), clamp(j,0,2)] reflected.
  // Cleaner: build a 4×4 array first.
  // Linear-extension lookup with recursion across BOTH axes so
  // virtual corner points (e.g. (-1, -1)) extrapolate diagonally.
  // Without the recursion, the corner would only extend along one
  // axis and the cubic interpolant wouldn't reduce to linear on an
  // identity grid — observable as a ~1% deviation at interior
  // sample points.
  const get = (i: number, j: number): Point2 => {
    if (i < 0) {
      const a = get(0, j);
      const b = get(1, j);
      return { x: 2 * a.x - b.x, y: 2 * a.y - b.y };
    }
    if (i > 2) {
      const a = get(2, j);
      const b = get(1, j);
      return { x: 2 * a.x - b.x, y: 2 * a.y - b.y };
    }
    if (j < 0) {
      const a = get(i, 0);
      const b = get(i, 1);
      return { x: 2 * a.x - b.x, y: 2 * a.y - b.y };
    }
    if (j > 2) {
      const a = get(i, 2);
      const b = get(i, 1);
      return { x: 2 * a.x - b.x, y: 2 * a.y - b.y };
    }
    return grid[i * 3 + j];
  };

  // 3 control points span [0, 1]; convert (u, v) to grid
  // coordinates (gu, gv) ∈ [0, 2]. Locate the cell + local t.
  const gu = u * 2;
  const gv = v * 2;
  const iu = Math.min(1, Math.max(0, Math.floor(gu)));
  const iv = Math.min(1, Math.max(0, Math.floor(gv)));
  const tu = gu - iu;
  const tv = gv - iv;

  // Build 4 row-samples by interpolating along u in each of 4 rows
  // (indices iv-1, iv, iv+1, iv+2). Then interpolate along v.
  const rowSample = (rowIdx: number): Point2 => {
    const p0 = get(rowIdx, iu - 1);
    const p1 = get(rowIdx, iu);
    const p2 = get(rowIdx, iu + 1);
    const p3 = get(rowIdx, iu + 2);
    return {
      x: catmullRom1D(p0.x, p1.x, p2.x, p3.x, tu),
      y: catmullRom1D(p0.y, p1.y, p2.y, p3.y, tu),
    };
  };
  const r0 = rowSample(iv - 1);
  const r1 = rowSample(iv);
  const r2 = rowSample(iv + 1);
  const r3 = rowSample(iv + 2);
  return {
    x: catmullRom1D(r0.x, r1.x, r2.x, r3.x, tv),
    y: catmullRom1D(r0.y, r1.y, r2.y, r3.y, tv),
  };
}

export interface RasterizeWarpOptions {
  /** Sub-cells per axis (default 32). Each cell renders as 2
   *  affine triangles, so total triangles = 2 × subdivs². Cost
   *  is O(subdivs²); 32 = 1024 cells = 2048 triangles ≈ acceptable
   *  on 4K canvases. */
  subdivisions?: number;
}

/** Render `source` onto `target` warped through the 3×3 grid.
 *  The source's full rect maps to the deformed mesh in target
 *  coords. */
export function rasterizeWarp(
  source: HTMLCanvasElement | HTMLImageElement,
  grid: WarpGrid,
  target: HTMLCanvasElement,
  opts: RasterizeWarpOptions = {},
): void {
  const ctx = target.getContext('2d');
  if (!ctx) return;
  const sw = source instanceof HTMLCanvasElement ? source.width : source.naturalWidth;
  const sh = source instanceof HTMLCanvasElement ? source.height : source.naturalHeight;
  if (sw <= 0 || sh <= 0) return;
  const subdivs = Math.max(1, Math.floor(opts.subdivisions ?? 32));

  // Pre-evaluate destination points at every (subdivs+1)² sub-cell
  // corner. ~1KB at default subdiv; the alternative is two
  // interpolateWarp calls per sub-cell which doubles the work.
  const dst: Point2[] = new Array((subdivs + 1) * (subdivs + 1));
  for (let i = 0; i <= subdivs; i++) {
    for (let j = 0; j <= subdivs; j++) {
      dst[i * (subdivs + 1) + j] = interpolateWarp(grid, j / subdivs, i / subdivs);
    }
  }

  ctx.save();
  for (let i = 0; i < subdivs; i++) {
    for (let j = 0; j < subdivs; j++) {
      // Source-space sub-cell corners.
      const x0 = (j * sw) / subdivs;
      const x1 = ((j + 1) * sw) / subdivs;
      const y0 = (i * sh) / subdivs;
      const y1 = ((i + 1) * sh) / subdivs;
      const s00 = { x: x0, y: y0 };
      const s10 = { x: x1, y: y0 };
      const s11 = { x: x1, y: y1 };
      const s01 = { x: x0, y: y1 };
      // Destination corners from the pre-computed grid.
      const d00 = dst[i * (subdivs + 1) + j];
      const d10 = dst[i * (subdivs + 1) + (j + 1)];
      const d11 = dst[(i + 1) * (subdivs + 1) + (j + 1)];
      const d01 = dst[(i + 1) * (subdivs + 1) + j];
      drawWarpedTriangle(ctx, source, s00, s10, s11, d00, d10, d11);
      drawWarpedTriangle(ctx, source, s00, s11, s01, d00, d11, d01);
    }
  }
  ctx.restore();
}

function drawWarpedTriangle(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement | HTMLImageElement,
  s0: Point2,
  s1: Point2,
  s2: Point2,
  d0: Point2,
  d1: Point2,
  d2: Point2,
): void {
  const M = affineFromTriangle(s0, s1, s2, d0, d1, d2);
  if (!M) return;
  ctx.save();
  // Expand the clip path outward from the centroid so neighboring
  // triangles overlap at their shared edges — kills visible seam
  // artifacts. Adaptive cap on the expand magnitude so the
  // expansion never exceeds a fraction of the triangle's shortest
  // edge — without this, very-small dst triangles (small-bbox
  // layers in scenes that get preview-downscaled to tiny dst
  // quads) get expanded past their own size and the clip path
  // inverts to zero / negative area, causing no pixels to be
  // drawn. See `projective-transform.ts`'s `drawAffineTriangle`
  // for the full rationale.
  const e01 = Math.hypot(d1.x - d0.x, d1.y - d0.y);
  const e12 = Math.hypot(d2.x - d1.x, d2.y - d1.y);
  const e20 = Math.hypot(d0.x - d2.x, d0.y - d2.y);
  const minEdge = Math.min(e01, e12, e20);
  const seamOverlapPx = Math.min(1.5, Math.max(0.25, minEdge * 0.25));
  const cx = (d0.x + d1.x + d2.x) / 3;
  const cy = (d0.y + d1.y + d2.y) / 3;
  const expand = (p: Point2): Point2 => {
    const dx = p.x - cx;
    const dy = p.y - cy;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return p;
    return {
      x: p.x + (dx / len) * seamOverlapPx,
      y: p.y + (dy / len) * seamOverlapPx,
    };
  };
  const e0 = expand(d0);
  const e1 = expand(d1);
  const e2 = expand(d2);
  ctx.beginPath();
  ctx.moveTo(e0.x, e0.y);
  ctx.lineTo(e1.x, e1.y);
  ctx.lineTo(e2.x, e2.y);
  ctx.closePath();
  ctx.clip();
  ctx.transform(M[0], M[1], M[2], M[3], M[4], M[5]);
  ctx.drawImage(source, 0, 0);
  ctx.restore();
}

export type WarpPreset = 'arc' | 'bulge' | 'twist' | 'wave';

/** Seed a warp grid with a preset shape. `intensity` ∈ [0, 1]
 *  scales the deformation. The grid is overwritten — caller
 *  passes width/height of the source rect (= the dimensions the
 *  identity grid would have).
 *
 *  Preset semantics:
 *    - arc: top edge curves up (or down at intensity < 0). Like
 *      Photoshop's "Arc" warp preset.
 *    - bulge: middle row + col pull outward toward image bounds.
 *    - twist: middle row rotates around the center.
 *    - wave: top-and-bottom rows oscillate in opposite phase.
 *
 *  All presets preserve the corner positions — only edges and
 *  center move — so the layer's bounding box stays anchored. */
export function applyWarpPreset(
  width: number,
  height: number,
  preset: WarpPreset,
  intensity: number,
): WarpGrid {
  const t = Math.max(-1, Math.min(1, intensity));
  const base = defaultWarpGrid(width, height);
  // Build a mutable grid we'll return as readonly.
  const g: Point2[] = base.map((p) => ({ x: p.x, y: p.y }));
  if (preset === 'arc') {
    const dy = -t * height * 0.3;
    // Top edge midpoint pulls up; corners stay; center + side
    // mids interpolate so the surface is smooth.
    g[1].y += dy;
    g[4].y += dy * 0.5;
    g[7].y += dy * 0.1;
  } else if (preset === 'bulge') {
    const dx = t * width * 0.2;
    const dy = t * height * 0.2;
    g[3].x -= dx; // left middle pulls left
    g[5].x += dx; // right middle pulls right
    g[1].y -= dy; // top middle pulls up
    g[7].y += dy; // bottom middle pulls down
  } else if (preset === 'twist') {
    // Rotate the middle row + column around the center by t*45°.
    const cx = width / 2;
    const cy = height / 2;
    const angle = t * (Math.PI / 4);
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const rotate = (p: Point2): Point2 => {
      const dx = p.x - cx;
      const dy = p.y - cy;
      return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
    };
    // Don't rotate corners; rotate the inner cross.
    g[1] = rotate(g[1]);
    g[3] = rotate(g[3]);
    g[5] = rotate(g[5]);
    g[7] = rotate(g[7]);
  } else if (preset === 'wave') {
    const dy = t * height * 0.15;
    // Top middle up, bottom middle down — sine-like wave.
    g[1].y -= dy;
    g[7].y += dy;
  }
  return g as unknown as WarpGrid;
}
